// Recover from stale Vite chunks after a deploy.
//
// When a tab stays open across a deploy, Vite's content-hashed chunk filenames
// change and the old asset files are removed from the server. A long-open tab
// that then lazy-loads a route chunk fetches a file that no longer exists, and
// the dynamic import() rejects (the router navigation silently aborts). The
// recovery is a one-time full page reload to the intended path, which re-fetches
// the current manifest and the new chunk names.

const RELOAD_KEY = 'viteChunkReloadedAt'
const RELOAD_WINDOW_MS = 10_000

// Matches the chunk-load failure messages across browsers:
//   Chromium: "error loading dynamically imported module"
//   Firefox:  "error loading dynamically imported module" / "Failed to fetch dynamically imported module"
//   Safari:   "Importing a module script failed"
export function isChunkLoadError(error) {
  const msg = error?.message || String(error || '')
  return /error loading dynamically imported module|Failed to fetch dynamically imported module|Importing a module script failed/i.test(
    msg,
  )
}

// Reload once. Returns true if a reload was triggered, false if we already
// reloaded within the window (so a genuinely-broken chunk doesn't loop forever —
// the error then surfaces to Sentry/GlitchTip instead).
export function reloadOnce(targetPath) {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0)
  if (Date.now() - last < RELOAD_WINDOW_MS) return false
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()))
  if (targetPath) {
    location.assign(targetPath)
  } else {
    location.reload()
  }
  return true
}

// Wire the guards onto a vue-router instance + the window. Idempotent enough for
// a single boot; not designed to be installed twice.
export function installChunkReloadGuards(router) {
  router.onError((error, to) => {
    if (isChunkLoadError(error)) reloadOnce(to?.fullPath)
  })

  // Clear the guard once any navigation succeeds, so a future deploy's stale
  // chunk can recover again.
  router.afterEach(() => {
    sessionStorage.removeItem(RELOAD_KEY)
  })

  // Vite's preload helper can throw before the router's onError sees it.
  addEventListener('vite:preloadError', (event) => {
    if (reloadOnce()) event.preventDefault()
  })
}
